/**
 * Carpool Management helpers — paths, dates (yyyy-MM-dd strings and yyyyMMdd ints), privacy
 * guards for free-text notes, and re-exports of the shared Forms helpers (stamps, errors,
 * relative time, initials) so the module has one import point.
 *
 * Stamps on the wire are yyyyMMddHHmmss numbers already localised to the caller.
 */

import {
  RIDES_VERIFICATION_STATUS,
  RIDES_BOOKING_STATUS,
  RIDES_AVAILABILITY_STATUS,
  RIDES_DISPUTE_STATUS,
  RIDES_VERIFICATION_TYPE,
  RIDES_SECURITY_CHECK_TYPE,
  RIDES_SEX,
  RIDES_UPLOAD_STATUS,
  RIDES_UPLOAD_TYPE,
  RIDES_UPLOAD_CONTENT_TYPES,
  RIDES_REQUIREMENT_LEVEL,
} from '@actschurch/ccms-sdk';

export {
  RIDES_VERIFICATION_STATUS,
  RIDES_BOOKING_STATUS,
  RIDES_AVAILABILITY_STATUS,
  RIDES_DISPUTE_STATUS,
  RIDES_VERIFICATION_TYPE,
  RIDES_SECURITY_CHECK_TYPE,
  RIDES_SEX,
  RIDES_UPLOAD_STATUS,
  RIDES_UPLOAD_TYPE,
  RIDES_UPLOAD_CONTENT_TYPES,
  RIDES_REQUIREMENT_LEVEL,
};

export {
  toStamp,
  stampToDate,
  formatStamp,
  formatStampDateTime,
  formatDayLabel,
  errorMessage,
  errorStatus,
} from '../../FormsManagement/components/formsUtils';
export { humanize, relativeTime, initials, colorFor, pct, plural } from '../../FormsManagement/components/formsDisplay';

export const RIDES_BASE_PATH = '/app/rides';

export const driverPath = (userId: string, tab?: string) =>
  `${RIDES_BASE_PATH}/drivers/${encodeURIComponent(userId)}${tab ? `/${tab}` : ''}`;

export const DOCUMENTS_PATH = `${RIDES_BASE_PATH}/documents`;
export const settingsPath = (tab?: string) => `${RIDES_BASE_PATH}/admin/settings${tab ? `?tab=${tab}` : ''}`;
export const copyPath = (search?: string) => `${RIDES_BASE_PATH}/admin/copy${search ? `?q=${encodeURIComponent(search)}` : ''}`;

// ---------------------------------------------------------------- yyyy-MM-dd dates

const pad = (n: number) => String(n).padStart(2, '0');

/** Date → yyyy-MM-dd (local). */
export function dateToYmd(date: Date | null | undefined): string | undefined {
  if (!date || Number.isNaN(date.getTime())) return undefined;
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/** yyyy-MM-dd (or yyyyMMdd int) → local Date at midnight. */
export function ymdToDate(value: string | number | null | undefined): Date | null {
  if (value === null || value === undefined || value === '') return null;
  const s = String(value).replace(/-/g, '');
  if (s.length < 8) return null;
  const d = new Date(Number(s.slice(0, 4)), Number(s.slice(4, 6)) - 1, Number(s.slice(6, 8)));
  return Number.isNaN(d.getTime()) ? null : d;
}

export const todayYmd = () => dateToYmd(new Date()) as string;

export function addDays(date: Date, days: number): Date {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
}

/** "Sun 12 Oct 2026" */
export function formatYmd(value: string | number | null | undefined, fallback = 'No date'): string {
  const d = ymdToDate(value);
  return d ? d.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' }) : fallback;
}

/** "12 Oct" */
export function formatYmdShort(value: string | number | null | undefined, fallback = 'No date'): string {
  const d = ymdToDate(value);
  return d ? d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' }) : fallback;
}

/** "October 2026" */
export function formatMonth(date: Date): string {
  return date.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
}

/** Whole days from today until a yyyy-MM-dd date (negative = past). */
export function daysUntilYmd(value: string | number | null | undefined): number | null {
  const d = ymdToDate(value);
  if (!d) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.round((d.getTime() - today.getTime()) / 86_400_000);
}

/** Expiry label for passports / licences. */
export function expiryInfo(value: string | null | undefined): { label: string; color: string } | null {
  const n = daysUntilYmd(value);
  if (n === null) return null;
  if (n < 0) return { label: `Expired ${formatYmdShort(value)}`, color: 'red' };
  if (n <= 30) return { label: `Expires in ${n} day${n === 1 ? '' : 's'}`, color: 'orange' };
  return { label: `Expires ${formatYmd(value)}`, color: 'gray' };
}

/** "08:00" → "08:00"; tolerates "08:00:00". */
export function formatTime(value: string | null | undefined, fallback = 'No time'): string {
  if (!value) return fallback;
  const m = /^(\d{1,2}):(\d{2})/.exec(value);
  return m ? `${pad(Number(m[1]))}:${m[2]}` : value;
}

// ---------------------------------------------------------------- privacy guards

/**
 * Free-text notes must never carry an identity number. A South African ID is 13 digits;
 * spaces and dashes are stripped before the check so "900101 5009 087" is still caught.
 */
export function containsIdNumber(text: string | null | undefined): boolean {
  if (!text) return false;
  return /\d{13}/.test(text.replace(/[\s-]/g, ''));
}

export const ID_NUMBER_WARNING = 'Notes must not contain an ID, passport or licence number. Record the outcome only.';

// ---------------------------------------------------------------- uploaded documents

/** "820 KB", "1.4 MB" */
export function formatBytes(bytes: number | null | undefined): string {
  if (!bytes || bytes <= 0) return 'Size unknown';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export const isPdfContentType = (contentType: string | null | undefined) => (contentType ?? '').toLowerCase().includes('pdf');

// ---------------------------------------------------------------- vehicles

export function vehicleLabel(v: { make?: string; model?: string; colour?: string; label?: string } | null | undefined): string {
  if (!v) return 'No vehicle';
  const name = (v.label || [v.make, v.model].filter(Boolean).join(' ')).trim();
  return [name, v.colour].filter(Boolean).join(' · ') || 'No vehicle details';
}

export function partyLabel(b: { partySize: number; partyMaleCount: number; partyFemaleCount: number }): string {
  const parts: string[] = [];
  if (b.partyMaleCount > 0) parts.push(`${b.partyMaleCount} ${b.partyMaleCount === 1 ? 'man' : 'men'}`);
  if (b.partyFemaleCount > 0) parts.push(`${b.partyFemaleCount} ${b.partyFemaleCount === 1 ? 'woman' : 'women'}`);
  return parts.length ? parts.join(', ') : `${b.partySize} ${b.partySize === 1 ? 'person' : 'people'}`;
}

/** Plain-language text for a rule code (never show the raw code to admins either). */
export const RULE_CODE_LABELS: Record<string, string> = {
  format_invalid: 'Number format is not valid',
  luhn_failed: 'Check digit does not match',
  date_invalid: 'Date of birth in the number is not a real date',
  age_out_of_range: 'Age from the document is out of range',
  citizenship_invalid: 'Citizenship digit is not valid',
  digit12_invalid: 'Digit 12 is not valid',
  dob_mismatch: 'Date of birth does not match the member profile',
  dob_corrected: 'Profile date of birth was corrected from the ID (check it against the document)',
  sex_mismatch: 'Sex does not match the declared sex',
  driver_under_18: 'Driver is under 18',
  guardian_is_minor: 'Guardian is under 18',
  document_expired: 'Document has expired',
  expiry_required: 'Expiry date is required',
  country_required: 'Issuing country is required',
  mrz_check_digit_failed: 'MRZ check digit does not match',
  sa_passport_pattern_warning: 'Number does not look like an SA passport (warning only)',
  licence_code_not_allowed: 'Licence code is not allowed for Carpool',
  foreign_licence_needs_english_or_idp: 'Foreign licence needs to be in English or carry an IDP',
  document_in_use: 'This document is already linked to another account',
};

export const ruleLabel = (code: string | null | undefined) => (code ? RULE_CODE_LABELS[code] || code.replace(/_/g, ' ') : '');
